import React, { useCallback, useEffect, useState } from 'react';

import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Button,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Snackbar,
  SnackbarCloseReason,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import DownloadIcon from '@mui/icons-material/Download';
import { useTranslation } from 'react-i18next';

import {
  DataGridPremium,
  GridActionsCellItem,
  GridColDef,
  GridRowParams,
} from '@mui/x-data-grid-premium';
import { ApiConsumer } from '../../../../../api/hexa/ApiConsumer';
import { ApiPdf } from '../../../../../api/hexa/ApiPdf';
import KeyFieldComponent from '../../../../components/KeyField';
import ConfirmContent from '../../../../components/ConfirmContent';
import { Consumer } from '../../../../../api/hexa/models/ConsumerModels';

interface ActionDialog {
  open: boolean;
  action: string;
  param?: string | number;
}

interface ConsumerDetailProps {
  customerId: string | number;
  applicationId: string | number;
}

const ConsumerDetail = (props: ConsumerDetailProps) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [consumers, setConsumers] = useState<Consumer[]>([]);
  const { customerId, applicationId } = props;
  const [consumerSecret, setConsumerSecret] = useState('');
  const [consumerKey, setConsumerKey] = useState('');
  const [pdfPassword, setPdfPassword] = useState('');
  const [actionDialog, setActionDialog] = useState<ActionDialog>({
    open: false,
    action: '',
  });
  const [error, setError] = useState({
    open: false,
    message: '',
  });
  const [pdfModalOpen, setPdfModalOpen] = useState(false);
  const [pdfFormData, setPdfFormData] = useState({
    ragioneSociale: '',
    endUser: '',
    indirizzo: '',
    localita: '',
    nazione: '',
    language: 'it',
  });
  const columns: GridColDef<Consumer>[] = [
    {
      field: 'key',
      headerName: t('applications.detail.consumer.key'),
      flex: 2,
    },
    {
      field: 'active',
      headerName: t('applications.detail.consumer.status'),
      flex: 1,
      renderCell: (params: any) => {
        return (
          <Tooltip
            title={
              !!params.value
                ? t('applications.detail.consumer.active')
                : t('applications.detail.consumer.inactive')
            }
          >
            <Switch
              size="small"
              checked={!!params.value}
              onChange={(event, checked) =>
                onStatusToggleChange(params.row.id, checked)
              }
            />
          </Tooltip>
        );
      },
    },
    {
      field: 'actions',
      type: 'actions',
      getActions: (params: GridRowParams) => [
        <GridActionsCellItem
          icon={
            <Tooltip title={t('applications.detail.consumer.reset')}>
              <RefreshIcon color="primary" />
            </Tooltip>
          }
          onClick={() =>
            setActionDialog({
              open: true,
              action: 'reset',
              param: params.row.id,
            })
          }
          label={t('applications.detail.consumer.reset')}
        />,
      ],
    },
  ];

  /**
   * Reset the consumer secret
   */
  const resetSecret = useCallback(
    async (rowId: string | number) => {
      setLoading(true);
      try {
        const consumer: Consumer = await ApiConsumer.update(
          customerId,
          applicationId,
          rowId,
          {
            reset: true,
          }
        );
        setConsumerKey(consumer.key ?? '');
        setConsumerSecret(consumer.secret ?? '');
      } catch (error: any) {
        setError({
          open: true,
          message: `Error ${error.status ?? 500} resetting secret: 
          ${error.response?.data.error ?? 'Error resetting secret'}`,
        });
      } finally {
        setLoading(false);
      }
    },
    [customerId, applicationId]
  );

  /**
   * Fetch the consumer details from the API
   */
  const fetchConsumerDetails = useCallback(async () => {
    setLoading(true);
    try {
      const consumers = await ApiConsumer.index(customerId, applicationId);
      setConsumers(consumers);
    } catch (error: any) {
      setError({
        open: true,
        message: `Error ${error.status ?? 500} fetching consumer: 
        ${error.response?.data.error ?? 'Error fetching consumer'}`,
      });
    } finally {
      setLoading(false);
    }
  }, [customerId, applicationId]);

  useEffect(() => {
    fetchConsumerDetails();
  }, [fetchConsumerDetails]);

  /**
   * Create and shows in dedicated fields a new pair of consumer key and secret
   */
  const handleCreateKeys = useCallback(async () => {
    if (customerId == null || applicationId == null) {
      return;
    }
    setLoading(true);
    try {
      const res = await ApiConsumer.create(customerId, applicationId);
      setConsumerSecret((res as Consumer).secret ?? '');
      setConsumerKey((res as Consumer).key ?? '');
    } catch (error: any) {
      setError({
        open: true,
        message: `Error ${error.status ?? 500} creating keys: 
        ${error.response?.data.error ?? 'Error creating keys'}`,
      });
    } finally {
      setActionDialog((prevState) => ({ ...prevState, open: false }));
      setLoading(false);
    }
  }, [customerId, applicationId]);

  /**
   * Update the consumer active status
   */
  const onStatusToggleChange = useCallback(
    async (rowId: string | number, toggleValue: boolean) => {
      setLoading(true);
      try {
        const res: Consumer = await ApiConsumer.update(
          customerId,
          applicationId,
          rowId,
          {
            active: toggleValue,
          }
        );
        //Visually update the toggle in the grid
        let newConsumers = consumers.map((row) =>
          row.id === rowId ? res : row
        );
        setConsumers(newConsumers);
      } catch (error: any) {
        setError({
          open: true,
          message: `Error ${error.status ?? 500} changing active status: 
          ${error.response?.data.error ?? 'Error changing active status'}`,
        });
      } finally {
        setLoading(false);
      }
    },
    [customerId, consumers, applicationId]
  );

  /**
   * Open PDF modal to collect additional data
   */
  const handleDownloadPdf = useCallback(async () => {
    if (!consumerKey || !consumerSecret) {
      setError({
        open: true,
        message: 'Consumer key and secret are required for PDF generation',
      });
      return;
    }

    setPdfModalOpen(true);
  }, [consumerKey, consumerSecret]);

  /**
   * Download PDF with all collected data
   */
  const handleConfirmDownloadPdf = useCallback(async () => {
    setPdfLoading(true);
    try {
      const { blob: pdfBlob, password } = await ApiPdf.consumerCredentials({
        consumerKey: consumerKey,
        consumerSecret: consumerSecret,
        ragioneSociale: pdfFormData.ragioneSociale,
        endUser: pdfFormData.endUser,
        indirizzo: pdfFormData.indirizzo,
        localita: pdfFormData.localita,
        nazione: pdfFormData.nazione,
        language: pdfFormData.language,
      });
            
      if (password) {
        setPdfPassword(password);
      }
      
      const url = window.URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `consumer-credentials-${consumerKey}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setPdfModalOpen(false);
      setPdfFormData({
        ragioneSociale: '',
        endUser: '',
        indirizzo: '',
        localita: '',
        nazione: '',
        language: 'it',
      });

    } catch (error: any) {
      setError({
        open: true,
        message: 'Error downloading PDF',
      });
    } finally {
      setPdfLoading(false);
    }
  }, [consumerKey, consumerSecret, pdfFormData]);

  const handlePdfFormChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setPdfFormData(prev => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const handleClosePdfModal = () => {
    setPdfModalOpen(false);
    setPdfFormData({
      ragioneSociale: '',
      endUser: '',
      indirizzo: '',
      localita: '',
      nazione: '',
      language: 'it',
    });
  };

  const handleSnackbarClose = (
    event: React.SyntheticEvent | Event,
    reason?: SnackbarCloseReason
  ) => {
    if (reason === 'clickaway') {
      return;
    }
    setError({ ...error, open: false });
  };

  const ConfirmActionDialog = (
    <Dialog
      disableEnforceFocus={true}
      disableRestoreFocus={true}
      onClose={() =>
        setActionDialog((prevState) => ({ ...prevState, open: false }))
      }
      open={actionDialog.open}
      fullWidth
      sx={{
        '& .MuiPaper-root': {
          borderRadius: '0.8rem',
        },
      }}
    >
      <DialogContent>
        <ConfirmContent
          dialogTitle={`applications.detail.consumer.confirm_${actionDialog.action}`}
          onSaveClick={() => {
            if (actionDialog.action === 'reset' && actionDialog.param)
              resetSecret(actionDialog.param);
            else handleCreateKeys();
            setActionDialog((prevState) => ({ ...prevState, open: false }));
          }}
          onDiscardClick={() =>
            setActionDialog((prevState) => ({ ...prevState, open: false }))
          }
        />
      </DialogContent>
    </Dialog>
  );

  const PdfDataDialog = (
    <Dialog
      open={pdfModalOpen}
      onClose={handleClosePdfModal}
      fullWidth
      maxWidth="sm"
      sx={{
        '& .MuiPaper-root': {
          borderRadius: '0.8rem',
        },
      }}
    >
      <DialogTitle>
        {t('applications.detail.consumer.pdf_data_title')}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            fullWidth
            label={t('applications.detail.consumer.ragione_sociale')}
            value={pdfFormData.ragioneSociale}
            onChange={handlePdfFormChange('ragioneSociale')}
            required
          />
          <TextField
            fullWidth
            label={t('applications.detail.consumer.end_user')}
            value={pdfFormData.endUser}
            onChange={handlePdfFormChange('endUser')}
          />
          <TextField
            fullWidth
            label={t('applications.detail.consumer.indirizzo')}
            value={pdfFormData.indirizzo}
            onChange={handlePdfFormChange('indirizzo')}
            required
          />
          <TextField
            fullWidth
            label={t('applications.detail.consumer.localita')}
            value={pdfFormData.localita}
            onChange={handlePdfFormChange('localita')}
            required
          />
          <TextField
            fullWidth
            label={t('applications.detail.consumer.nazione')}
            value={pdfFormData.nazione}
            onChange={handlePdfFormChange('nazione')}
            required
          />

          <FormControl component="fieldset">
            <FormLabel component="legend">
              {t('applications.detail.consumer.language')}
            </FormLabel>
            <RadioGroup
              row
              name="language"
              value={pdfFormData.language}
              onChange={handlePdfFormChange('language')}
            >
              <FormControlLabel
                value="it"
                control={<Radio />}
                label={t('applications.detail.consumer.language_it')}
              />
              <FormControlLabel
                value="en"
                control={<Radio />}
                label={t('applications.detail.consumer.language_en')}
              />
            </RadioGroup>
          </FormControl>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClosePdfModal} color="secondary">
          {t('common.cancel')}
        </Button>
        <LoadingButton
          onClick={handleConfirmDownloadPdf}
          loading={pdfLoading}
          color="primary"
          variant="contained"
          disabled={
            !pdfFormData.ragioneSociale ||
            !pdfFormData.indirizzo ||
            !pdfFormData.localita ||
            !pdfFormData.nazione
          }
        >
          {t('applications.detail.consumer.download_pdf')}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );

  return (
    <>
      <Container
        sx={{
          gap: '1rem',
          display: 'flex',
          flexDirection: 'column',
          paddingX: '1rem !important',
          maxWidth: '100% !important',
        }}
      >
        <Typography
          variant="h5"
          sx={{ paddingY: '0 !important', paddingLeft: '0 !important' }}
        >
          {t('applications.detail.consumer.title')}
        </Typography>
        <Stack direction="row" justifyContent="flex-end">
          <LoadingButton
            loading={loading}
            onClick={() =>
              setActionDialog({ open: true, action: 'create_keys' })
            }
            color="secondary"
            variant="outlined"
          >
            {t('applications.detail.consumer.create_keys')}
          </LoadingButton>
        </Stack>
      </Container>
      <Container
        sx={{
          paddingTop: '0 !important',
          paddingX: '1rem !important',
          marginTop: '1rem',
          maxWidth: '100% !important',
        }}
      >
        {(consumerKey || consumerSecret) && (
          <Stack mb={2} spacing={2}>
            {consumerKey && (
              <KeyFieldComponent
                label="applications.detail.consumer.key"
                keyValue={consumerKey}
              />
            )}
            {consumerSecret && (
              <KeyFieldComponent
                label="applications.detail.consumer.secret"
                keyValue={consumerSecret}
              />
            )}
            {pdfPassword && (
              <KeyFieldComponent
                label="applications.detail.consumer.pdf_password"
                keyValue={pdfPassword}
              />
            )}
            {consumerKey && consumerSecret && (
              <Stack direction="row" justifyContent="flex-end">
                <LoadingButton
                  loading={pdfLoading}
                  onClick={handleDownloadPdf}
                  color="primary"
                  variant="contained"
                  startIcon={<DownloadIcon />}
                  disabled={loading}
                >
                  {t('applications.detail.consumer.download_pdf')}
                </LoadingButton>
              </Stack>
            )}
          </Stack>
        )}
      </Container>
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        <DataGridPremium
          sx={{
            marginX: '1rem',
            marginBottom: '2rem',
          }}
          getRowId={(row) => row.id}
          rows={consumers}
          autoHeight={true}
          columns={columns}
        />
      </div>
      <Snackbar
        open={error.open}
        autoHideDuration={5000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity="error">
          {error.message}
        </Alert>
      </Snackbar>
      {ConfirmActionDialog}
      {PdfDataDialog}
    </>
  );
};

export default ConsumerDetail;
